/**
 * Lazy-loads OneDoc booking widgets when they enter the viewport, and shows
 * a fallback message if a widget fails to signal successful load in time.
 *
 * Deliberately separate from the vendor-supplied OneDoc script (which stays
 * untouched, minified, inline in the Fluid template): this only interacts
 * with it from the outside, via the DOM and the same "message" channel.
 */
import {onDomReady} from "../../../shared/ts/helpers/on-dom-ready";

const FALLBACK_TIMEOUT_MS = 10000;

function initWidget(iframe: HTMLIFrameElement) {
  const widgetId = iframe.dataset["widgetId"];
  const src = iframe.dataset["src"];
  if (!widgetId || !src) {
    return;
  }

  // Strip data-src now (before window-load) so the vendor script's own
  // window-load handler can't eager-load an off-screen widget.
  delete iframe.dataset["src"];

  const fallback = document.getElementById(`od-widget-fallback-${widgetId}`);
  let loadTimeout: number | null = null;

  const showFallback = () => {
    iframe.style.display = "none";
    if (fallback) {
      fallback.hidden = false;
    }
  };

  const startWidget = () => {
    iframe.src = src;
    loadTimeout = window.setTimeout(showFallback, FALLBACK_TIMEOUT_MS);
  };

  // Independent of the vendor script's own message listener: only watches
  // for this widget's success signal to cancel the failure fallback.
  window.addEventListener("message", (event) => {
    if (loadTimeout !== null && event.data && event.data["od-widget-id"] === widgetId) {
      window.clearTimeout(loadTimeout);
      loadTimeout = null;
    }
  });

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          startWidget();
        }
      });
    });
    observer.observe(iframe);
  } else {
    startWidget();
  }
}

function init() {
  document.querySelectorAll<HTMLIFrameElement>("iframe.od-widget")
    .forEach(initWidget);
}

onDomReady(init);
